/**
 * laneMoney.tsx — the pipeline board's per-lane MONEY footer (2026-09-06, CTO).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE MODEL: THE APPROVAL IS THE ECONOMIC EVENT, NOT THE PAYMENT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * We owe a clinician for an approval whether or not the patient pays, so an
 * approved card carries value in EVERY lane — and each lane is answering a
 * different question, which is the whole reason this is a per-lane footer and
 * not one number on the toolbar:
 *
 *  · Won      — REALISED. One month of the current fee, over plans still billing.
 *  · Lost     — TWO NUMBERS THAT ARE NEVER ADDED: revenue forfeited (what we
 *               expected FROM THE PATIENT) and, separately, the clinician fees
 *               we owe anyway. Cash that LEFT against revenue that never ARRIVED.
 *  · anywhere else holding an approved card — PENDING potential.
 *
 * ⚠️ WHY THIS FILE EXISTS AT ALL, RATHER THAN LIVING IN PipelineBoard.tsx.
 * Two reasons, and the second is the load-bearing one:
 *  1. `feat/card-rebuild-wave` is rewriting the board's lane furniture in
 *     parallel. Keeping the footer out of that file reduces this change's
 *     footprint there to an import, a prop and one line of JSX.
 *  2. THE ROLE GATE IS TESTABLE ON ITS OWN. `laneMoneyFor` below is the single
 *     decision about whether money renders, and it can be asserted directly
 *     rather than only through a rendered board.
 *
 * ⚠️ ADMIN ONLY, AND DEFENCE IN DEPTH. "Clinicians never see money" is enforced
 * in three independent places and none of them is redundant:
 *  · clinic-service declares the read `@Roles('ADMIN')` — a clinician gets a
 *    403, not a redacted body. THAT is the control (R41).
 *  · PipelineTab does not fire the read for a clinician at all.
 *  · `laneMoneyFor` refuses to render for a non-ADMIN even if data somehow
 *    arrived.
 * The portal's money-redaction sweep would NOT have caught a leak here: its
 * test stub strips price fields before the portal ever sees them, so it tests
 * the SERVER's guard rather than this component's. The board is therefore
 * rendered AS A CLINICIAN in the specs, asserting the footer is absent.
 *
 * ⚠️ THE DEDICATED ROW IS ADMIN-ONLY TOO, AND THAT IS THE WHOLE POINT OF §3
 * BELOW. Every lane an ADMIN sees carries a cell; a CLINICIAN gets no cell, no
 * empty cell, and no strip. The gate is answered ONCE, at the top of
 * `laneMoneyFor`, before anything reads pence — so "always render a cell" and
 * "clinicians never see money" cannot come apart.
 *
 * ⚠️ EVERY FIGURE OUTSIDE "WON" IS AN ESTIMATE AND SAYS SO ON THE TILE. There
 * is no order behind those cards, so there is no term and no price: the
 * clinician's recommendation carries product keys only. The server prices the
 * cheapest recommended product off the same plan-price matrix the admin pricing
 * screen edits, and `assumedTermMonths` rides the response so this file never
 * hardcodes a 3.
 */
import type { ReactNode } from 'react';
import type { DealLaneMoney, DealLaneMoneyResponse, DealStage, UserRole } from '../api/types';
import { formatPence } from '../lib/format';
import { isLostStage } from './stages';

/**
 * WHAT ONE LANE'S CELL IS, BEFORE ANY OF IT IS PAINTED.
 *
 * ⚠️ THREE OUTCOMES, NOT TWO, AND COLLAPSING ANY PAIR OF THEM IS A LIE
 * (2026-09-07, CTO). This used to be `DealLaneMoney | null`, which forced three
 * genuinely different situations through one "no" and rendered nothing for all
 * of them:
 *
 *  · `none`    — there is no row on this board AT ALL: not an ADMIN, or the
 *                money read never landed. The pipeline is how staff work and a
 *                money panel must never be able to take it down, so a failed
 *                read leaves the board exactly as it was: no strip, no eleven
 *                columns of apology.
 *  · `absent`  — an ADMIN, the read landed, and THIS LANE IS NOT IN IT. The
 *                portal and clinic-service deploy independently, so a payload
 *                can predate a newly added stage (the same deploy window the
 *                `?? 0` on the badge count exists for). That is not zero and it
 *                is not a dead source — it is a lane the server has not been
 *                taught about yet, and it gets to say so.
 *  · `figures`  — the lane is in the payload. `approvals` MAY BE 0, and that is
 *                deliberate: see the zero cell below.
 */
export type LaneMoneyCell =
  | { kind: 'none' }
  | { kind: 'absent' }
  | { kind: 'figures'; figures: DealLaneMoney };

/**
 * The ONE decision about whether a lane shows money, and the only thing that
 * may make it.
 *
 * ⚠️ IT NO LONGER REFUSES A LANE WITH NO APPROVALS (2026-09-07, CTO reviewed
 * the live board: "that entire row should be dedicated to that, even if there
 * are no values in certain columns"). The old refusal is kept here as the trap
 * it turned out to be — it read "a row of £0 across eleven columns is noise
 * that trains people to stop reading the footer", which is a fair worry about
 * DECORATION and the wrong call about a ROW. A strip with holes in it does not
 * read as eleven answers of which some are zero; it reads as a rendering
 * accident, and the eye cannot run along it. HubSpot puts `£0 · Total amount`
 * in every column of its board for exactly this reason.
 *
 * ⚠️ WHAT DID NOT CHANGE, AND MUST NOT: `sources.* === 'unavailable'` STILL
 * NEVER RENDERS AS £0. An unreachable service and an empty lane produce the
 * same number and only one of them is a fact. That distinction is now three
 * distinct cells (zero / absent / unavailable) rather than one shared blank.
 */
export function laneMoneyFor(
  lane: DealStage,
  money: DealLaneMoneyResponse | null,
  role: UserRole,
): LaneMoneyCell {
  // ⚠️ THE ROLE CHECK IS FIRST AND IS NOT AN OPTIMISATION. Everything below it
  // reads pence. Follow the precedent the drawer already sets for its Payments
  // tab (`role === 'ADMIN' && deal.orderCaseRef`): decide once, at the top.
  // It is also what makes the dedicated row safe — "every lane gets a cell" is
  // scoped by this line and cannot escape it.
  if (role !== 'ADMIN') return { kind: 'none' };
  if (!money) return { kind: 'none' };
  // ⚠️ OPTIONAL CHAIN, and it is not defensive noise. `lanes` is REQUIRED by the
  // type, so `tsc` will not warn — but the value crosses a network boundary from
  // a service that deploys independently of this app, and a payload that
  // deserialises without it takes the WHOLE BOARD down rather than one cell.
  // Every other read on this path already degrades (an absent lane, an
  // unavailable source); this was the one that threw. Found by review, 2026-09-07.
  const forLane = money.lanes?.[lane];
  if (!forLane) return { kind: 'absent' };
  return { kind: 'figures', figures: forLane };
}

/**
 * ⚠️ THE TOTAL IS PINNED TO THE LANE FLOOR (2026-09-06, CTO photographed both
 * failures on the same board).
 *
 * This used to be a plain block rendered BELOW the lane's card scroller. Two
 * things were wrong with that, and neither was cosmetic:
 *
 *  1. IT SAT UNDER THE FOLD ON A FULL LANE. The scroller was capped at 62vh and
 *     the footer began after it, so the one figure that describes the WHOLE
 *     lane was the one thing you had to scroll the page to reach — on a board
 *     whose entire purpose is answering "where is the money" at a glance.
 *  2. THE BOARD'S OWN HORIZONTAL SCROLLBAR LANDED ON IT. That scrollbar is
 *     drawn at the bottom of the grid holding all eleven lanes, which is
 *     exactly where the tallest lane's last element ends. The total was being
 *     clipped by a scrollbar.
 *
 * ⚠️ A FIXED-HEIGHT BAR AT THE LANE FLOOR — NOT A STICKY PIN. This paragraph
 * argued the opposite until 2026-09-06 and the reasoning is kept because it is
 * the trap: it said a three-card lane "should show its total under the third
 * card, not floating at the bottom of an otherwise empty column", and that
 * `sticky` gives both readings from one declaration. Both true, and both the
 * wrong goal. The CTO's requirement is that the amounts be scannable ACROSS the
 * board — "at the same level towards the bottom in one row" — and a sticky
 * footer parks against ITS OWN scroller, so eleven lanes of eleven heights gave
 * eleven totals at eleven levels. A total under the third card is worth nothing
 * if the eye cannot run along the row.
 *
 * ⚠️ THE FILL IS NO LONGER `--gps-surface`, AND THAT WAS A REAL DEFECT
 * (2026-09-07, CTO: "it is white on white… I want a small highlight on that
 * cell"). The old declaration matched the Card behind the lane exactly, and the
 * comment defended it: the pin should read "as the lane's own floor and not as
 * a tinted overlay laid on top of it". That reasoning was written for a STICKY
 * pin passing over cards, where the fill's job was to hide what scrolled under
 * it and looking like the lane was the point. Nothing scrolls under this any
 * more — it is a sibling below a bounded scroller — so all that reasoning
 * bought was #ffffff drawn on #ffffff: a cell you cannot see, held together by
 * a 1px rule.
 *
 * It is now the APPROVED CHIP'S OWN GREEN TINT PAIR (`--gps-primary-light`
 * behind, `--gps-primary-tint-strong` as the edge) — the board's existing
 * light-theme tint, not a new colour. Measured against the white lane it sits
 * in: 1.125:1, which is a panel you can see and not one that shouts. The two
 * alternatives in the token set were measured and rejected: `--gps-surface-2`
 * is 1.055:1 (that is the defect again, 4% quieter) and
 * `--gps-primary-tint-strong` as a FILL is 1.299:1, which pulls the eye off the
 * cards. Every text colour on the tile was re-checked against the tint rather
 * than against white: muted is 5.01:1 and `--gps-text` is 14.54:1, so the
 * hierarchy below is AA at every level.
 */
/**
 * ⚠️ EVERY FOOTER IS THE SAME HEIGHT, AND THAT IS WHAT PUTS THE £ FIGURES ON
 * ONE LINE — the bottom-aligned BOX was not enough.
 *
 * Measured in Chrome on the first attempt at this: the footer boxes all
 * bottom-aligned correctly at y=871.9, and the amounts still landed at 832.8
 * (Won), 815.3 (the eight standard lanes) and 788.8 (Lost) — a 44px spread.
 * The boxes are 46.1 / 63.6 / 90.1px tall because Lost carries a second figure
 * and a fee line, so bottom-aligning them puts their TOPS 44px apart, and the
 * headline figure is the first child of each. Levelling the container is not
 * levelling the number, and the number is the thing being scanned across.
 *
 * Fixed height + `alignContent: 'start'`: the tops align, so the first row of
 * every footer aligns, so the amounts align. The slack falls to the bottom of
 * the shorter footers where there is nothing to misread.
 *
 * ⚠️ THE THREE-TIER CELL MADE THIS PROPERTY STRUCTURAL RATHER THAN COUNTED
 * (2026-09-07). Every cell is now LABEL, then FIGURE, then caption — the same
 * two elements in the same order, emitted by the one `LaneCell` below, whatever
 * the lane is saying. So the figure is row 2 of every tile and the things that
 * used to be able to un-level it cannot reach it: a longer caption, a second
 * figure, a fee line and the unpriced admission all sit BELOW the figure now.
 * The one remaining way to push it off the row is a LABEL that takes two lines,
 * which is why the label is `nowrap` and ellipsises (see `labelStyle`).
 *
 * ⚠️ 102px IS MEASURED, NOT CHOSEN, AND THE NUMBER HAS NOW BEEN WRONG THREE
 * TIMES — every time by measuring a variant that was not the tallest REAL one.
 * 92px was a Lost footer measured without the `unpricedApprovals` caption.
 * 108px held until the three-tier cell added the label row. Then 116px, and
 * that one was wrong in the OPPOSITE direction: it was measured against a
 * WRAPPED term caption, a variant the wire cannot produce (see `captionStyle`),
 * so every lane on the board carried ~14px of dead space to insure against a
 * line that never wraps. That is the white space the CTO photographed under
 * every cell — "get to the maximum needed height and then remove the white
 * spaces that you have there."
 *
 * RE-DERIVED FROM SCRATCH in Chrome at the real 280px lane track, with the real
 * Fraunces 600 / DM Sans 700 loaded over HTTP, over EVERY variant the component
 * can emit — and with `min-height` dropped to 0 so each cell reports the height
 * it actually needs:
 *
 *   3-line tile — absent · zero approvals · won · won-with-dead-orders ·
 *                 pending · pending-with-dead-prices · duplicate ·
 *                 unreachable                                      62.44px
 *   4-line tile — pending + "N approvals not priced"                77.08px
 *   4-line tile — Lost + fee line                                   86.08px
 *   5-line tile — Lost + unpriced + fee                            100.72px  ← THE MAXIMUM
 *   5-line tile — Lost + unpriced + "Clinician fees unavailable"   100.72px
 *
 * ⚠️ THE WRAPPED CASE WAS CHECKED BEFORE BEING BUDGETED FOR, AND IT DOES NOT
 * HAPPEN. At the real 264px content width the widest caption the wire can
 * produce ("£99,999.99 estimated at 12 months · 128 approvals") runs 245.22px,
 * and a deliberately absurd "£999,999.99 … 1024 approvals" runs 258.88px —
 * both single lines. The other two optional rows are nowhere near: the unpriced
 * admission is 125.78px at its widest and the fee line 201.53px. A wrap is now
 * also IMPOSSIBLE rather than merely unobserved, because every line is
 * `nowrap` + ellipsis — which is what makes 100.72px a property of the markup
 * instead of a fact about this month's figures.
 *
 * WHERE ALL 100.72px GO, so the next person can re-derive it without a browser:
 *   7.00  padding-top          13.00  label      (10px   x 1.3)
 *   1.00  border-top           19.80  figure     (18px   x 1.1)
 *   6.00  padding-bottom       13.64  caption    (10.5px x 1.3)
 *   4.00  4 x 1px grid gap     13.64  unpriced   (10.5px x 1.3)
 *   4.00  fee margin-top       18.64  fee line   (13.64 + 4 pad + 1 rule)
 *
 * 102px covers that with 1.28px to spare — the same order of headroom the old
 * number was signed off with (1.6px), kept because the line heights above are
 * fractional and round PER LINE under browser zoom, and the failure mode is not
 * a clipped cell but a `minHeight` box that GROWS and lifts one lane's figure
 * out of the row. It is NOT rounded up further: every pixel here comes off the
 * card scroller above it.
 *
 * ⚠️ ADD A LINE TO ANY VARIANT AND RE-MEASURE. `laneMoney.spec` §5 counts the
 * DIRECT children against `LANE_FOOTER_MAX_LINES` so a sixth cannot land
 * unnoticed, but jsdom cannot tell you the new pixel height — only that you owe
 * a look. The harness that produced the numbers above is the one described in
 * the repo's CLAUDE.md: render these components with `renderToStaticMarkup`
 * into a page that links the real `tokens.css` and the real @fontsource faces,
 * serve it over HTTP (a `file://` page never loads them) and read
 * `getBoundingClientRect()`.
 */
const LANE_FOOTER_HEIGHT = '102px';

/**
 * The line budget `LANE_FOOTER_HEIGHT` was measured against — five, which is
 * what the tallest variant (Lost + unpriced + fee) emits.
 *
 * Exported for the spec: jsdom has no layout, so counting DIRECT children is
 * the only guard available here against a variant quietly growing past the
 * fixed height and dropping its amount out of the row.
 *
 * ⚠️ ITS JOB IS TO FAIL WHEN A SIXTH ROW LANDS, and it is tied to a height that
 * is now 102px rather than 116px — so the slack it used to be able to absorb is
 * gone. A sixth line costs 13.64px against 1.28px of headroom: it WILL grow the
 * box and un-level the row. The guard is therefore stricter in effect than it
 * was, without changing its number, and `laneMoney.spec` §5 also asserts that
 * some variant actually REACHES five — a budget nothing reaches would pass
 * while the tallest cell was quietly deleted.
 */
export const LANE_FOOTER_MAX_LINES = 5;

/**
 * ══════════════════════════════════════════════════════════════════════════
 * THE FOUR TONES — the cell is coloured by WHAT THE LANE MEANS (2026-09-07, CTO
 * reviewed the tinted row on stage: "yellow pending, green won, and red lost,
 * kind of an indication there. If there are zero, then it can be greyed out —
 * that indicates there's nothing").
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every cell was `--gps-primary-light` before this, which painted the LOST lane
 * in the colour of a win. On a board people scan for money that is not a
 * cosmetic flaw: green is the estate's "realised/approved" hue everywhere else,
 * so a green forfeit figure reads as revenue.
 *
 * ⚠️ `nothing` IS THE TONE WHENEVER THERE IS NO FIGURE, NOT ONLY WHEN THE LANE
 * IS EMPTY — and that is a deliberate widening of the CTO's "if there are
 * zero". It covers five branches: an empty lane (£0, a fact), a lane the server
 * did not send, and each of the three dead sources. The reason they share a
 * tone is that they share the thing the tone is describing: THE FIGURE SLOT IS
 * NOT AN AMOUNT. Four of the five print a muted "—" for exactly that reason
 * (see `absentFigureStyle`), and a red or amber panel behind a muted dash
 * contradicts it — it asserts an outcome for a number we do not have. Grey
 * agrees with the dash.
 *
 * ⚠️ SO GREY MEANS TWO THINGS — "nothing here" and "we do not know" — AND THE
 * TEXT IS WHAT SEPARATES THEM. That is acceptable only because it always has:
 * every cell states its own label and caption in words, and the two readings
 * never share wording. "No approvals / Nothing approved in this lane" is the
 * fact; "Not reported / This lane was not in the money read", "Realised
 * unavailable / Orders source did not answer" and "Potential unavailable /
 * Price source did not answer" are the unknowns. A reader who cannot see colour
 * at all loses nothing here, which is the WCAG 1.4.1 test (colour is never the
 * only carrier) and also the honest test: if the tone were load-bearing, an
 * outage and an empty lane would be indistinguishable, and that is the exact
 * defect `laneMoneyFor`'s three-outcome cell was built to prevent.
 *
 * ⚠️ RED IS KEYED ON `isLostStage`, NEVER ON "TERMINAL", AND THE DIFFERENCE IS
 * A PATIENT. `closed_duplicate` is terminal and is NOT a loss: it is a real
 * person who submitted twice, whose actual outcome lives on the canonical card.
 * Painting it red books a loss against somebody who is being served on the
 * other card — see the LOSS CONTRACT block on `LOSS_STAGES` in stages.ts, which
 * exists precisely so this question has one answer and is not re-litigated at
 * each call site. `unreachable` is not a loss either, and it is not terminal;
 * both fall to `pending`, which is true of them — an unreachable patient is
 * still a patient we could convert.
 *
 * This file used to answer it with an inline `lane === 'closed_lost'`, which is
 * the pattern that constant was created to replace. It is now `isLostStage`, so
 * a stage joining the loss contract colours this row without a portal edit.
 */
type LaneTone = 'won' | 'pending' | 'lost' | 'nothing';

/**
 * Tone → its ground and edge. BOTH come from the tint pairs in tokens.css, and
 * the type on top is UNCHANGED in all four — see the measured table there.
 *
 * ⚠️ NOTHING HERE TOUCHES THE INK, ON PURPOSE, AND IT IS BOTH AN ACCESSIBILITY
 * AND A DESIGN DECISION. Making the lost figure red as well as its panel would
 * make it the loudest thing on the board — and the CTO's brief is the opposite
 * ("slightly red… a tint, not an alarm. This is a routine reporting figure on a
 * board, not an error state. If it shouts, the genuinely urgent things on this
 * board stop being noticed"). It also keeps the type hierarchy identical in all
 * eleven columns, which is what the level-row property below depends on: one
 * ink, one size, one weight per tier, whatever the tone.
 */
const TONE_STYLE: Record<LaneTone, { background: string; borderTop: string }> = {
  won: {
    background: 'var(--gps-primary-light)',
    borderTop: '1px solid var(--gps-primary-tint-strong)',
  },
  pending: {
    background: 'var(--gps-warn-bg)',
    borderTop: '1px solid var(--gps-warn-tint-strong)',
  },
  // NOT `--gps-danger-bg`: that tint is a CHIP ground carrying its own dark red
  // ink, and `--gps-muted` on it is 4.285:1 — an AA failure at this cell's 10px
  // label and 10.5px captions. `--gps-danger-tint` is the panel-safe step of the
  // same hue (4.702:1), and is also the "slightly red" the CTO asked for.
  lost: {
    background: 'var(--gps-danger-tint)',
    borderTop: '1px solid var(--gps-danger-tint-strong)',
  },
  // NOT `--gps-surface-2`: at 1.055:1 against the white surface it is the
  // invisible-cell defect this row was tinted to fix (4% quieter than the green
  // that was already called "white on white"). `--gps-neutral-tint` is 1.124:1,
  // matching the won tone's presence, so an empty lane still reads as a cell.
  nothing: {
    background: 'var(--gps-neutral-tint)',
    borderTop: '1px solid var(--gps-neutral-tint-strong)',
  },
};

const footerStyle = {
  minHeight: LANE_FOOTER_HEIGHT,
  // Content to the TOP of that fixed box — the whole point is that the first
  // two rows land at the same y in every lane.
  alignContent: 'start',
  // NOT `position: sticky` any more (CTO, 2026-09-06). This is the lane's last
  // flex child in a lane of FIXED height, so the layout already puts it on the
  // floor — and every lane being the same height is what lands all eleven of
  // these on ONE LINE across the board. A sticky pin cannot do that: it parks
  // against its own scroller, so eleven lanes of different heights gave eleven
  // totals at different levels. Re-adding `sticky` here does nothing on its own
  // and re-breaks the row the moment the lane stops being a bounded column.
  display: 'grid',
  gap: '1px',
  marginTop: '6px',
  // Horizontal padding, which the flat pin never had: the tint makes this a
  // PANEL, and type running into the edge of a panel reads as a clipping bug.
  // 8px lands the label's left edge under the card text above it — the card is
  // inset 4px by the scroller's padding and another 10px by its own, so 8px is
  // the closest this can sit to that line without a negative margin.
  padding: '7px 8px 6px',
  // ⚠️ NO `background` AND NO `borderTop` HERE ANY MORE — both come from
  // `TONE_STYLE` and are spread over this object per cell (2026-09-07). They
  // were a single hard-coded green pair, which is what painted a LOST lane in
  // the colour of a win. `--gps-border` was never a candidate for the edge: it
  // is 1.00–1.24:1 on the four grounds, i.e. invisible on all of them.
  //
  // ⚠️ THE WHITE BEHIND THIS CELL IS NOT THE LANE: the lane track is
  // TRANSPARENT, and the surface comes from `PipelineTab`'s outer `Card`. Worth
  // knowing before anyone "fixes" a tint's contrast by colouring the lane
  // instead — that would repaint the whole column.
  //
  // A panel, so it has corners. Bottom-only: the top edge butts against the
  // lane's own rule and a radius there would open two white nicks beside it.
  borderRadius: '0 0 var(--gps-r-sm) var(--gps-r-sm)',
  boxShadow: 'var(--gps-shadow-up)',
  lineHeight: 1.3,
  color: 'var(--gps-muted)',
} as const;

/**
 * TIER 1 — THE LABEL. Small, uppercase, letter-spaced, quiet.
 *
 * It is the lane header's idiom, one step down: the header is 12.5px/700 at
 * 0.03em, this is 10px/700 at 0.07em, because tracking has to open up as type
 * shrinks or uppercase closes into a block. Reading the board top to bottom you
 * therefore get the same kind of label at the top of a lane and at its foot,
 * which is what makes the strip read as part of the board rather than as
 * something bolted under it.
 *
 * ⚠️ `nowrap` + ellipsis IS THE ONE THING HOLDING THE ROW LEVEL, and it is not
 * a tidiness preference. The figure's y position is decided entirely by how
 * tall row 1 is. A label that wrapped to two lines in one lane would push that
 * lane's figure ~13px below the other ten — the 44px-spread defect again, one
 * row further up and harder to spot because ten of the eleven would still
 * agree. Truncating a label is recoverable; un-levelling the row is the bug
 * this whole element exists to fix.
 */
const labelStyle = {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--gps-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

/**
 * TIER 2 — THE FIGURE. The one thing on the tile that shouts.
 *
 * The display face, because that is what the portal uses everywhere a number
 * is the message (the plan cards, the BMI dial, the projection) and this is the
 * board's equivalent. 18px against the label's 10px and the caption's 10.5px is
 * the hierarchy the CTO signed off on the scaffolding — "you have emphasised
 * the figures and placed the text" — and the live version had all three at
 * roughly one weight, so nothing led.
 *
 * ⚠️ `fontWeight: 600`, NOT 700 OR 800. `main.tsx` bundles Fraunces at 400 and
 * 600 only. Asking for 700 gets a SYNTHESISED bold — the browser smears the
 * 400 outline — which at 18px on a pale tint looks muddy next to the genuinely
 * bold 700 label beside it in DM Sans. 600 is a real cut.
 *
 * `tabular-nums` so the eleven figures line up digit-for-digit down the strip;
 * proportional figures make a row of amounts look ragged even when it is level.
 */
const figureStyle = {
  fontFamily: 'var(--gps-font-display)',
  fontSize: '18px',
  fontWeight: 600,
  lineHeight: 1.1,
  color: 'var(--gps-text)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} as const;

/**
 * The figure slot when there is NO FIGURE — an em dash, and deliberately NOT
 * in the figure's colour.
 *
 * ⚠️ THE POINT IS THAT IT MUST NOT READ AS AN AMOUNT. This slot carries "—" in
 * exactly the three cases where printing a number would be a lie: a lane the
 * server did not send, and a dead prices or orders source. `--gps-text` at
 * 14.54:1 is the emphasis that says "this is the answer"; the muted step says
 * "there is no answer, and the label above tells you why". "£0" here, or a
 * confident-looking dash, is the failure — an empty price matrix and an
 * unreachable prices-service produce the same £0 and only one of them is a
 * fact.
 */
const absentFigureStyle = {
  ...figureStyle,
  color: 'var(--gps-muted)',
} as const;

/**
 * TIER 3 — the caption. The quietest thing on the tile.
 *
 * ⚠️ IT IS `nowrap` + ELLIPSIS NOW, AND ITS OLD COMMENT WAS THE REASON THE ROW
 * WAS 15px TOO TALL (2026-09-07). That comment read: "the only line allowed to
 * wrap: it sits BELOW the figure, so its height cannot move the figure off the
 * shared row." The first half was a licence nothing needed and the second half
 * is FALSE, in a way that only shows up at the one moment it matters:
 *
 *  · The licence cost 15px of every lane. `LANE_FOOTER_HEIGHT` had to cover a
 *    WRAPPED caption (114.4px) rather than the tallest real cell (100.7px), so
 *    every lane on the board carried ~14px of white space to insure against a
 *    line that — measured — never wraps. The widest caption the wire can
 *    produce runs 245.2px at the real 264px content width, and even an absurd
 *    "£999,999.99 estimated at 12 months · 1024 approvals" is 258.9px. That
 *    insurance was the dead space the CTO photographed.
 *  · And the height is `minHeight`, not `height`, so a caption that DID wrap
 *    would not overflow — the BOX WOULD GROW. The lane is a fixed-height flex
 *    column whose scroller has `min-height: 0`, so a taller footer moves its
 *    own TOP up, which moves the FIGURE up, which is exactly the un-levelled
 *    row this element exists to prevent. Silently, in one lane out of eleven.
 *
 * So the wrap is forbidden instead of budgeted for. That turns 100.7px from an
 * observation about today's data into a property of the markup: five single
 * lines, whatever the server sends. The full text rides `title`, the pattern the
 * board card's truncated email already uses — the truncation costs pixels, not
 * information, and the value stays in the accessibility tree.
 */
const captionStyle = {
  fontSize: '10.5px',
  color: 'var(--gps-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

/**
 * The clinician bill sits behind its own rule, ALWAYS.
 *
 * ⚠️ THE RULE IS THE POINT, not decoration. The two Lost numbers are different
 * kinds of money pointing in opposite directions and adding them double-counts
 * the same lost deal. Putting the fee in the same visual stack as the forfeit
 * is exactly how somebody ends up reading them as one column to total.
 */
const feeStyle = {
  marginTop: '4px',
  paddingTop: '4px',
  borderTop: '1px dashed var(--gps-border-strong)',
  fontSize: '10.5px',
  color: 'var(--gps-text)',
  fontVariantNumeric: 'tabular-nums',
  // Single-line for the same reason as the caption above: the fixed height is a
  // `minHeight`, so any line that wraps GROWS the cell and lifts the figure out
  // of the row. This one has far more headroom (201.5px of text at its most
  // absurd, against 264px), but "every line is one line" is what makes the
  // measured maximum a guarantee rather than a fact about today's figures.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

/** `n` of a thing, pluralised — "1 approval", "4 approvals". */
const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * ONE CELL, AND EVERY CELL IN THE ROW IS THIS.
 *
 * ⚠️ IT IS A SHARED COMPONENT RATHER THAN A SHARED STYLE OBJECT ON PURPOSE.
 * The property the row depends on is ORDINAL, not visual: the label has to be
 * child 1 and the figure child 2 in all eleven columns, or the figures are not
 * on one line. A style object cannot promise that and a reviewer cannot check
 * it by eye across six branches — emitting the two rows from one place can, and
 * the extra lines a lane needs are `children`, which land underneath where they
 * cannot reach the figure.
 */
function LaneCell({
  lane,
  tone,
  label,
  figure,
  noFigure = false,
  caption,
  children,
}: {
  lane: DealStage;
  /** ⚠️ REQUIRED, WITH NO DEFAULT, AND THAT IS THE GUARD. Every branch below
   *  has to state what its lane MEANS, and `tsc` fails the build if a new one
   *  forgets. A default would silently give the next variant somebody adds
   *  whichever tone happened to be first in the union — which is how every cell
   *  came to be green in the first place. */
  tone: LaneTone;
  label: string;
  figure: string;
  /** The figure slot is a placeholder, not an amount — render it muted. */
  noFigure?: boolean;
  caption: string;
  children?: ReactNode;
}) {
  return (
    <div
      // The tone pair is spread LAST so a ground can only ever be set here, and
      // `footerStyle` no longer carries one to be accidentally overridden.
      style={{ ...footerStyle, ...TONE_STYLE[tone] }}
      data-testid={`lane-money-${lane}`}
      // ⚠️ THE TONE IS ASSERTABLE WITHOUT READING COLOURS. jsdom resolves no
      // `var()`, so a spec that read `style.background` would compare two
      // literal token STRINGS and pass while the token itself was wrong or
      // missing. This attribute lets the spec assert the DECISION ("closed_lost
      // is the lost tone") and leaves the token→pixel half to the browser
      // measurement, which is the only place it can be checked.
      data-tone={tone}
    >
      <span style={labelStyle}>{label}</span>
      {/* Its own testid: this is the element the browser measurement reads a
          `getBoundingClientRect()` off to prove all eleven are on one baseline,
          and jsdom does no layout, so the spec has nothing else to aim at. */}
      <span style={noFigure ? absentFigureStyle : figureStyle} data-testid={`lane-figure-${lane}`}>
        {figure}
      </span>
      {/* `title` carries the untruncated caption — see `captionStyle`. */}
      <span style={captionStyle} title={caption}>
        {caption}
      </span>
      {children}
    </div>
  );
}

export function LaneMoneyFooter({
  lane,
  money,
  role,
  assumedTermMonths,
  sources,
}: {
  lane: DealStage;
  money: DealLaneMoneyResponse | null;
  role: UserRole;
  assumedTermMonths: number;
  sources: DealLaneMoneyResponse['sources'];
}) {
  const cell = laneMoneyFor(lane, money, role);

  // ── NO ROW AT ALL: not an ADMIN, or the read never landed ──────────────────
  // ⚠️ A CLINICIAN GETS NOTHING — not a cell, not an empty cell, not a strip.
  // This is the only `return null` in the file and it is the whole role gate's
  // rendering half.
  if (cell.kind === 'none') return null;

  // ── THE LANE IS NOT IN THE PAYLOAD ────────────────────────────────────────
  // Not zero, and not a dead source: a stage this build of the board knows
  // about and the server's money read does not (independent deploys). It still
  // holds its place in the row, because a hole in the strip reads as a bug and
  // "£undefined" reads as a worse one.
  if (cell.kind === 'absent') {
    return (
      <LaneCell
        lane={lane}
        // NOTHING, not pending: the lane may well be full of approvals — we
        // simply were not told. An amber "pending" here would assert an
        // expectation the payload never made.
        tone="nothing"
        label="Not reported"
        figure="—"
        noFigure
        caption="This lane was not in the money read"
      />
    );
  }

  const figures = cell.figures;
  const approvals = plural(figures.approvals, 'approval', 'approvals');

  // ── NO APPROVED CARDS: £0, AND IT IS A FACT ───────────────────────────────
  // ⚠️ THIS BRANCH IS ABOVE EVERY SOURCE CHECK, AND THAT ORDER IS THE POINT.
  // `approvals` is clinic-service's OWN count of approval rows; it does not
  // come from prices, orders or rates. No approved cards means no expected
  // value however many downstream services are down, so this £0 is arithmetic
  // rather than a swallowed failure — the one zero on this panel that is not
  // hiding an unanswered question. Every other zero-looking outcome falls
  // through to a source check below and renders "—" instead.
  if (figures.approvals === 0) {
    return (
      <LaneCell
        lane={lane}
        // The CTO's own case for grey: "if there are zero, then it can be
        // greyed out — that indicates there's nothing". NOTE this beats the
        // lane's own meaning: an EMPTY closed_lost lane is grey, not red,
        // because £0 forfeited is not a loss to report.
        tone="nothing"
        label="No approvals"
        figure={formatPence(0)}
        caption="Nothing approved in this lane"
      />
    );
  }

  // ── WON: the only lane where the money is a FACT ──────────────────────────
  if (figures.realisedMonthlyPence !== null) {
    if (sources.orders === 'unavailable') {
      // NOT "£0". An unreachable orders-service and a lane where nothing is
      // billing produce the same total, and only one of them is a fact.
      return (
        <LaneCell
          lane={lane}
          // NOT green. Green is the claim "this money is realised", and we do
          // not know that — the caption says an outage, and the tone must not
          // out-rank the caption.
          tone="nothing"
          label="Realised unavailable"
          figure="—"
          noFigure
          caption={`Orders source did not answer · ${approvals}`}
        />
      );
    }
    return (
      <LaneCell
        lane={lane}
        // GREEN, and this is the only branch that earns it: the one figure on
        // the strip that is a fact rather than an estimate.
        tone="won"
        label="Realised, this month"
        figure={formatPence(figures.realisedMonthlyPence)}
        // "This month", not "lifetime", said TWICE around the figure — in the
        // label above it and again here. A patient can stop future months from
        // their own account and a 1-month plan does not renew, so an LTV
        // computed on a board read is stale immediately; the analytics suite
        // owns that figure, with the billing history. This is the one number on
        // the strip somebody would cheerfully multiply by twelve, so the
        // repetition is deliberate.
        caption={`One month, not lifetime · ${approvals}`}
      />
    );
  }

  // ── EVERY OTHER LANE: an expectation, and it says so ──────────────────────
  // ⚠️ `isLostStage`, NOT `lane === 'closed_lost'` AND NOT "is it terminal".
  // This drives both the red tone and the clinician-fee line, and
  // `closed_duplicate` is terminal without being a loss — see the LOSS CONTRACT
  // on `LOSS_STAGES` in stages.ts. The inline equality this replaces is exactly
  // the per-site re-litigation that constant exists to end.
  const lost = isLostStage(lane);
  if (sources.prices === 'unavailable') {
    return (
      <LaneCell
        lane={lane}
        // NOTHING even on the LOST lane, and deliberately so: red is a
        // statement about a forfeited AMOUNT, and there is no amount here. The
        // dead-source caption plus a muted "—" is the honest cell; a red panel
        // behind an em dash reads as a loss whose size was withheld. The lane
        // header above still says which lane this is.
        tone="nothing"
        label={lost ? 'Forfeited unavailable' : 'Potential unavailable'}
        figure="—"
        noFigure
        caption={`Price source did not answer · ${approvals}`}
      >
        <FeeLine figures={figures} lost={lost} sources={sources} />
      </LaneCell>
    );
  }

  return (
    <LaneCell
      lane={lane}
      // The two tones that carry a real estimate. RED for a booked loss, AMBER
      // for every other lane — including `closed_duplicate` and `unreachable`,
      // neither of which is a loss (see the tone block above).
      tone={lost ? 'lost' : 'pending'}
      // ⚠️ THE ESTIMATE MARKER IS IN THE LABEL NOW, ABOVE THE FIGURE — and the
      // caption below repeats it against its own number. The rule it is
      // honouring has not moved: BOTH figures on this tile are estimates and
      // each says so on its own line. Marking only the term figure would imply
      // the first month was booked, and marking only the headline would imply
      // the term figure was. Neither is: there is no order behind any of these
      // cards, and which of up to four recommended products the patient picks
      // is unknown.
      label={lost ? 'Forfeited, estimated' : 'First month, estimated'}
      figure={formatPence(figures.firstMonthPence ?? 0)}
      // The term comes off the wire. If the CTO's accepted assumption ever
      // moves, this line follows without a portal deploy.
      caption={`${formatPence(figures.threeMonthPence ?? 0)} estimated at ${assumedTermMonths} months · ${approvals}`}
    >
      {figures.unpricedApprovals > 0 ? (
        // The honest caveat. Without it a lane priced at half its approvals
        // reads as a small number rather than as a missing price row, and the
        // person acting on it has no way to tell the difference.
        <span style={captionStyle}>{plural(figures.unpricedApprovals, 'approval', 'approvals')} not priced</span>
      ) : null}
      <FeeLine figures={figures} lost={lost} sources={sources} />
    </LaneCell>
  );
}

/**
 * The clinician fee — Lost only, behind its own rule, and phrased so it can
 * never be read as part of the total above it.
 */
function FeeLine({
  figures,
  lost,
  sources,
}: {
  figures: DealLaneMoney;
  lost: boolean;
  sources: DealLaneMoneyResponse['sources'];
}) {
  if (!lost || figures.clinicianFeesPence === null) return null;
  if (sources.rates === 'unavailable') {
    // Muted, because it is an admission and not a figure — the same step the
    // "—" figure slot takes, for the same reason.
    return <span style={{ ...feeStyle, color: 'var(--gps-muted)' }}>Clinician fees unavailable</span>;
  }
  return (
    <span style={feeStyle}>
      {/* "Owed anyway" is the whole sentence in two words: we pay for the
          approval whether or not the patient ever did. */}
      {formatPence(figures.clinicianFeesPence)} clinician fees owed anyway
    </span>
  );
}
